import { ActionIcon, CSSProperties, ModalBaseStylesNames, ScrollArea, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { WarningIcon } from "@phosphor-icons/react";
import { ModalSettings, OpenContextModal } from "node_modules/@mantine/modals/lib/context";
import { ComponentProps } from "react";

import { AcceptTerms } from "@/components/@custom/modals/AcceptTermsModal";
import { DriverModal } from "@/components/@custom/modals/driver";
import { Expeditions } from "@/components/@custom/modals/expeditions";
import { PlateNotValid } from "@/components/@custom/modals/PlateNotValidModal";
import { SuccessQueueStatusModal } from "@/components/@custom/modals/queue/SuccessQueueStatusModal";
import { RebindTruckModal } from "@/components/@custom/modals/RebindTruckModal";
import { SaveQueueDeleteModal } from "@/components/@custom/modals/SaveQueueDeleteModal";
import { TermsModal } from "@/components/@custom/modals/TermsModal";
import { TransferCompany } from "@/components/@custom/modals/TransferCompanyModal";
import { cn } from "@/utils/cn";

export const MODALS = {
  rebindTruck: RebindTruckModal,
  transferCompany: TransferCompany,
  plateNotValid: PlateNotValid,
  acceptTerms: AcceptTerms,
  terms: TermsModal,
  driverEdit: DriverModal.Edit,
  driverDelete: DriverModal.Delete,
  expeditionsShipment: Expeditions.shipment,
  expeditionsConnectionStatus: Expeditions.connection.status,
  expeditionsQueueStatusScheduled: Expeditions.queue.status.scheduled,
  expeditionsQueueStatusCourtyard: Expeditions.queue.status.courtyard,
  expeditionsQueueStatusAttended: Expeditions.queue.status.attended,
  expeditionsQueueStatusMissing: Expeditions.queue.status.missing,
  expeditionsQueueStatusTons: Expeditions.queue.status.tons,
  expeditionsQueueStatusDropout: Expeditions.queue.status.dropout,
  expeditionsActionsEnableAttendance: Expeditions.actions.enable.attendance,
  expeditionsActionsEnableTolerance: Expeditions.actions.enable.tolerance,
  expeditionsActionsEnableDelay: Expeditions.actions.enable.delay,
  expeditionsActionsAttendanceShutdown: Expeditions.actions.attendance.shutdown,
  expeditionsActionsAttendanceBackToQueue: Expeditions.actions.attendance.backToQueue,
  saveQueueDelete: SaveQueueDeleteModal,
  successQueueStatus: SuccessQueueStatusModal
};

type Modals = typeof MODALS;
type ModalKey = keyof Modals;
type ModalByKey<T extends ModalKey> = Modals[T];
type ModalHandler<T extends ModalKey> = OpenContextModal<ComponentProps<ModalByKey<T>>["innerProps"]>["innerProps"];
type ModalOptions = Omit<OpenContextModal, "innerProps">;

const openTypedContextModal = <K extends ModalKey>(options: Omit<Parameters<typeof modals.openContextModal>[0], "modal"> & { modal: K }) =>
  modals.openContextModal({
    ...options,
    innerProps: options.innerProps ?? {}
  });

export const MODAL_HANDLER = {
  transferCompany: (props: ModalHandler<"transferCompany">, modalOptions?: ModalOptions) =>
    openTypedContextModal({
      modal: "transferCompany",
      classNames: {
        header: cn("bg-primary! justify-center!")
      },
      title: (
        <div className="flex gap-2 text-white">
          <WarningIcon weight="bold" size={32} />
          <h1 className="text-xl font-medium">Atenção!</h1>
        </div>
      ),
      withCloseButton: false,
      innerProps: props,
      ...modalOptions
    }),
  plateNotValid: (_props: ModalHandler<"plateNotValid">, _modalOptions?: ModalOptions) => {},
  acceptTerms: (props?: ModalHandler<"acceptTerms">, modalOptions?: ModalOptions) =>
    openTypedContextModal({
      modal: "acceptTerms",
      title: <h1 className="text-xl">Leia nossa politica de Privacidade e Termos de Uso</h1>,
      size: "xl",
      ...modalOptions,
      innerProps: props || { title: "", terms: "" }
    }),
  terms: (props?: ModalHandler<"terms">, modalOptions?: ModalOptions) =>
    openTypedContextModal({
      modal: "terms",
      title: <h1 className="text-xl">Politica de Privacidade e Termos de Uso</h1>,
      size: "xxl",
      withCloseButton: false,
      ...modalOptions,
      innerProps: props || { title: "", terms: "" }
    }),
  driver: {
    delete: (props: ModalHandler<"driverDelete">, modalOptions?: ModalOptions) =>
      openTypedContextModal({
        modal: "driverDelete",
        title: <h1 className="text-xl">Excluir Motorista</h1>,
        size: "md",
        innerProps: props,
        withCloseButton: true,
        ...modalOptions
      }),
    edit: (props?: ModalHandler<"driverEdit">, modalOptions?: ModalOptions) =>
      openTypedContextModal({
        modal: "driverEdit",
        // title: <h1 className="text-xl">Editar Motorista</h1>,
        size: "md",
        innerProps: props!,
        withCloseButton: false,
        ...modalOptions
      })
  },
  expeditions: {
    queue: {
      status: {
        scheduled: (props: ModalHandler<"expeditionsQueueStatusScheduled">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsQueueStatusScheduled",
            title: <h1 className="text-xl">Veículos</h1>,
            size: "md",
            innerProps: props,
            ...modalOptions
          }),
        courtyard: (props: ModalHandler<"expeditionsQueueStatusCourtyard">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsQueueStatusCourtyard",
            title: <h1 className="text-xl">Veículos</h1>,
            size: "md",
            innerProps: props,
            ...modalOptions
          }),
        attended: (props: ModalHandler<"expeditionsQueueStatusAttended">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsQueueStatusAttended",
            title: `Veículos Atendidos`,
            size: "xl",
            innerProps: props,
            scrollAreaComponent: ScrollArea.Autosize,
            styles: expeditionsQueueStatusTitleStyles({ header: { backgroundColor: "#32a457" } }),
            classNames: {
              close: cn("hover:bg-transparent!"),
              title: cn("text-2xl! font-semibold!")
            },
            ...modalOptions
          }),
        missing: (props: ModalHandler<"expeditionsQueueStatusMissing">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsQueueStatusMissing",
            title: `Veículos Faltosos`,
            size: "xl",
            innerProps: props,
            scrollAreaComponent: ScrollArea.Autosize,
            styles: expeditionsQueueStatusTitleStyles({ header: { backgroundColor: "#fc4040" } }),
            classNames: {
              close: cn("hover:bg-transparent!"),
              title: cn("text-2xl! font-semibold!")
            },
            ...modalOptions
          }),
        tons: (props: ModalHandler<"expeditionsQueueStatusTons">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsQueueStatusTons",
            title: <h1 className="text-xl">Veículos</h1>,
            size: "md",
            innerProps: props,
            ...modalOptions
          }),
        dropout: (props: ModalHandler<"expeditionsQueueStatusDropout">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsQueueStatusDropout",
            title: `Veículos Desistentes`,
            size: "xl",
            scrollAreaComponent: ScrollArea.Autosize,
            styles: expeditionsQueueStatusTitleStyles({ header: { backgroundColor: "#3c3c3b" } }),
            classNames: {
              close: cn("hover:bg-transparent!"),
              title: cn("text-2xl! font-semibold!"),
              body: cn("pt-(--mantine-spacing-md)!")
            },
            innerProps: props,
            ...modalOptions
          })
      }
    },
    connection: {
      status: (props?: ModalHandler<"expeditionsConnectionStatus">, modalOptions?: ModalOptions) =>
        openTypedContextModal({
          modal: "expeditionsConnectionStatus",
          size: "md",
          classNames: {
            header: cn("items-center! justify-center!"),
            title: cn("text-2xl!")
          },
          withCloseButton: false,
          centered: false,
          yOffset: "13dvh",
          innerProps: props!,
          ...modalOptions
        })
    },
    actions: {
      enable: {
        attendance: (props?: ModalHandler<"expeditionsActionsEnableAttendance">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsActionsEnableAttendance",
            size: "md",
            title: (
              <>
                <div className="flex h-16 w-16 flex-col items-center justify-center rounded-full bg-yellow-100 text-yellow-600">
                  <WarningIcon weight="bold" size={28} />
                </div>

                <div className="text-center">
                  <p className="font-semibold uppercase">Atenção!</p>
                </div>
              </>
            ),
            classNames: {
              title: cn("grid place-items-center text-xl!"),
              header: cn("items-center! justify-center!")
            },
            withCloseButton: false,
            innerProps: props!,
            ...modalOptions
          }),
        tolerance: (props?: ModalHandler<"expeditionsActionsEnableTolerance">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsActionsEnableTolerance",
            size: "md",
            title: (
              <Stack gap="0">
                <ActionIcon color="yellow" size="xl" radius="xl" variant="filled" className="self-center">
                  <WarningIcon weight="bold" size={28} />
                </ActionIcon>

                <Text fw="bold" className="uppercase">
                  Atenção!
                </Text>
              </Stack>
            ),
            classNames: {
              title: cn("grid place-items-center text-xl!"),
              header: cn("items-center! justify-center!")
            },
            innerProps: props!,
            withCloseButton: false,
            ...modalOptions
          }),
        delay: (props?: ModalHandler<"expeditionsActionsEnableDelay">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsActionsEnableDelay",
            size: "md",
            title: "Atrasar atendimento",
            classNames: {
              title: cn("text-xl!")
            },
            innerProps: props!,
            ...modalOptions
          })
      },
      attendance: {
        backToQueue: (props: ModalHandler<"expeditionsActionsAttendanceBackToQueue">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsActionsAttendanceBackToQueue",
            size: "md",
            title: "Deseja voltar o veículo para a fila?",
            classNames: {
              header: cn("bg-black!"),
              title: cn("text-2xl! text-white!"),
              close: cn("text-white! hover:bg-transparent! hover:text-white!")
            },
            innerProps: props,
            ...modalOptions
          }),
        shutdown: (props: ModalHandler<"expeditionsActionsAttendanceShutdown">, modalOptions?: ModalOptions) =>
          openTypedContextModal({
            modal: "expeditionsActionsAttendanceShutdown",
            size: "md",
            title: "Encerrar Atendimento",
            classNames: {
              header: cn("bg-green-600!"),
              title: cn("text-2xl! text-white!"),
              close: cn("text-white! hover:bg-transparent! hover:text-white!")
            },
            innerProps: props,
            ...modalOptions
          })
      }
    },
    shipment: (props: ModalHandler<"expeditionsShipment">, modalOptions?: ModalOptions) =>
      openTypedContextModal({
        modal: "expeditionsShipment",
        size: "md",
        title: "Carregamento",
        classNames: {
          header: cn("bg-black!"),
          title: cn("text-2xl! text-white!"),
          close: cn("text-white! hover:bg-transparent! hover:text-white!")
        },
        innerProps: props,
        ...modalOptions
      })
  },
  queue: {
    deleteQueue: (props?: ModalHandler<"saveQueueDelete">, modalOptions?: ModalOptions) =>
      openTypedContextModal({
        modal: "saveQueueDelete",
        size: "md",
        onClose: () => {
          modals.closeAll();
        },
        withCloseButton: false,
        innerProps: props,
        ...modalOptions
      }),
    successQueueStatus: (props?: ModalHandler<"successQueueStatus">, modalOptions?: ModalOptions) =>
      openTypedContextModal({
        modal: "successQueueStatus",
        fullScreen: true,
        withCloseButton: false,
        innerProps: props,
        zIndex: 9999999999,
        classNames: {
          content: cn(`grid! place-content-center bg-(--mantine-color-green-6)!`)
        },
        ...modalOptions
      })
  },
  rebindTruck: (props: ModalHandler<"rebindTruck">, modalOptions?: ModalOptions) =>
    openTypedContextModal({
      modal: "rebindTruck",
      size: "md",
      withCloseButton: false,
      innerProps: props,
      ...modalOptions
    })
};

const expeditionsQueueStatusTitleStyles = (styles?: Partial<Record<ModalBaseStylesNames, CSSProperties>>): Partial<Record<ModalBaseStylesNames, CSSProperties>> => ({
  header: {
    color: "#fff",
    ...styles?.header
  },
  close: {
    color: "#fff",
    "&:hover": {
      backgroundColor: "transparent",
      color: "#fff"
    }
  }
});

export const MODAL_PROPS: ModalSettings = { scrollAreaComponent: ScrollArea.Autosize, centered: true };
